import React, { useEffect, useMemo } from 'react';
import { 
  Box, 
  Flex, 
  Text, 
  IconButton, 
  Image, 
  VStack, 
  HStack,
  Tooltip
} from '@chakra-ui/react';
import { FaTimes } from 'react-icons/fa';
import { getFileIcon, formatFileSize, isImage } from '../utils/fileUtils';

interface AttachmentPreviewProps {
  files: File[];
  onRemoveFile: (index: number) => void;
}

const AttachmentPreview: React.FC<AttachmentPreviewProps> = ({ files, onRemoveFile }) => {
  const imageUrls = useMemo(() => {
    const urls = new Map<File, string>();
    files.forEach(file => {
      if (isImage(file.type)) {
        urls.set(file, URL.createObjectURL(file));
      }
    });
    return urls;
  }, [files]);

  useEffect(() => {
    return () => {
      imageUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [imageUrls]);

  if (files.length === 0) return null;

  return (
    <Box
      bg="gray.50"
      borderRadius="md"
      borderColor="gray.200"
      width="100%"
      maxH="200px"
      overflowY="auto"
    >
      <VStack spacing={1} align="stretch">
        {files.map((file, index) => (
          <Box
            key={`${file.name}-${file.size}-${index}`}
            bg="white"
            borderRadius="md"
            p={1}
            border="1px"
            borderColor="gray.200"
            position="relative"
          >
            <Flex align="center" gap={3}>
              <Box flexShrink={0}>
                {isImage(file.type) ? (
                  <Image
                    src={imageUrls.get(file) || ''}
                    alt={file.name}
                    boxSize="40px"
                    objectFit="cover"
                    borderRadius="md"
                    border="1px"
                    borderColor="gray.300"
                  />
                ) : (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    boxSize="40px"
                    bg="gray.100"
                    borderRadius="md"
                    border="1px"
                    borderColor="gray.300"
                  >
                    {React.createElement(getFileIcon(file.type, file.name).icon, { 
                      color: getFileIcon(file.type, file.name).color 
                    })}
                  </Box>
                )}
              </Box>

              <Box flex={1} minW={0}>
                <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                  {file.name}
                </Text>
                <HStack spacing={2}>
                  <Text fontSize="xs" color="gray.600">
                    {formatFileSize(file.size)}
                  </Text>
                </HStack>
              </Box>

              <Tooltip label="Remove attachment">
                <IconButton
                  aria-label="Remove attachment"
                  icon={<FaTimes />}
                  size="xs"
                  variant="ghost"
                  color="gray.500"
                  _hover={{ bg: 'red.100', color: 'red.600' }}
                  onClick={() => onRemoveFile(index)}
                />
              </Tooltip>
            </Flex>
          </Box>
        ))}
      </VStack>
    </Box>
  );
};

export default AttachmentPreview;